if (!customElements.get('aspadz-product-accordion')) {
  class AspadzProductAccordion extends HTMLElement {
    constructor() {
      super();
      this.panelAnimations = new WeakMap();
      this.handleClick = this.handleClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleMotionPreference = this.handleMotionPreference.bind(this);
    }

    connectedCallback() {
      if (this.isConnectedReady) return;

      this.isConnectedReady = true;
      this.items = Array.from(this.querySelectorAll('[data-aspadz-product-accordion-item]'));
      this.desktopMedia = Array.from(this.querySelectorAll('[data-aspadz-product-accordion-desktop-media]'));
      this.animationDuration = Number.parseInt(this.dataset.animationDuration, 10) || 0;
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion = this.motionPreference.matches;

      this.addEventListener('click', this.handleClick);
      this.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.motionPreference.addEventListener?.('change', this.handleMotionPreference);

      const initiallyExpanded = this.items.findIndex((item) => {
        return item.querySelector('[data-aspadz-product-accordion-trigger]')?.getAttribute('aria-expanded') === 'true';
      });

      this.setActive(initiallyExpanded >= 0 ? initiallyExpanded + 1 : 1, false);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      this.motionPreference?.removeEventListener?.('change', this.handleMotionPreference);
      this.isConnectedReady = false;
    }

    handleMotionPreference(event) {
      this.reducedMotion = event.matches;
    }

    handleClick(event) {
      const trigger = event.target.closest('[data-aspadz-product-accordion-trigger]');
      if (!trigger || !this.contains(trigger)) return;

      const index = Number.parseInt(trigger.dataset.aspadzProductAccordionTrigger, 10);
      if (trigger.getAttribute('aria-expanded') === 'true') return;

      this.setActive(index, true);
    }

    handleBlockSelect(event) {
      const item = event.target.closest('[data-aspadz-product-accordion-item]');
      if (!item || !this.contains(item)) return;

      this.setActive(Number.parseInt(item.dataset.aspadzProductAccordionItem, 10), true);
    }

    setActive(activeIndex, animate) {
      this.items.forEach((item) => {
        const index = Number.parseInt(item.dataset.aspadzProductAccordionItem, 10);
        const isActive = index === activeIndex;
        const trigger = item.querySelector('[data-aspadz-product-accordion-trigger]');
        const panel = item.querySelector('[data-aspadz-product-accordion-panel]');

        item.classList.toggle('is-active', isActive);
        trigger?.setAttribute('aria-expanded', isActive ? 'true' : 'false');

        if (!panel) return;
        if (isActive) {
          this.openPanel(panel, animate);
        } else {
          this.closePanel(panel, animate);
        }
      });

      this.desktopMedia.forEach((media) => {
        const index = Number.parseInt(media.dataset.aspadzProductAccordionDesktopMedia, 10);
        const isActive = index === activeIndex;
        media.classList.toggle('is-active', isActive);
        media.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      this.loadActiveMedia(activeIndex);
    }

    loadActiveMedia(activeIndex) {
      const activeRoots = [
        this.querySelector(`[data-aspadz-product-accordion-desktop-media="${activeIndex}"]`),
        this.querySelector(`[data-aspadz-product-accordion-item="${activeIndex}"]`),
      ];

      activeRoots.forEach((root) => {
        root?.querySelectorAll('source[data-srcset]').forEach((source) => {
          source.srcset = source.dataset.srcset;
          source.removeAttribute('data-srcset');
        });
      });
    }

    cancelPanelAnimation(panel) {
      const animation = this.panelAnimations.get(panel);
      if (!animation) return;

      animation.cancel();
      this.panelAnimations.delete(panel);
    }

    openPanel(panel, animate) {
      this.cancelPanelAnimation(panel);
      panel.hidden = false;

      if (!animate || this.reducedMotion || this.animationDuration === 0) {
        panel.style.removeProperty('height');
        panel.style.removeProperty('opacity');
        panel.style.removeProperty('overflow');
        return;
      }

      const targetHeight = panel.scrollHeight;
      const animation = panel.animate(
        [
          { height: '0px', opacity: 0 },
          { height: `${targetHeight}px`, opacity: 1 },
        ],
        {
          duration: this.animationDuration,
          easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        }
      );

      panel.style.overflow = 'hidden';
      this.panelAnimations.set(panel, animation);

      animation.addEventListener('finish', () => {
        if (panel.previousElementSibling?.querySelector('[aria-expanded="true"]')) {
          panel.style.removeProperty('height');
          panel.style.removeProperty('opacity');
          panel.style.removeProperty('overflow');
        }
        this.panelAnimations.delete(panel);
      });
    }

    closePanel(panel, animate) {
      if (panel.hidden) return;

      const startHeight = panel.getBoundingClientRect().height;
      this.cancelPanelAnimation(panel);

      if (!animate || this.reducedMotion || this.animationDuration === 0) {
        panel.hidden = true;
        panel.style.removeProperty('height');
        panel.style.removeProperty('opacity');
        panel.style.removeProperty('overflow');
        return;
      }

      const animation = panel.animate(
        [
          { height: `${startHeight}px`, opacity: 1 },
          { height: '0px', opacity: 0 },
        ],
        {
          duration: this.animationDuration,
          easing: 'cubic-bezier(0.64, 0, 0.78, 0)',
        }
      );

      panel.style.overflow = 'hidden';
      this.panelAnimations.set(panel, animation);

      animation.addEventListener('finish', () => {
        if (!panel.previousElementSibling?.querySelector('[aria-expanded="true"]')) {
          panel.hidden = true;
        }
        panel.style.removeProperty('height');
        panel.style.removeProperty('opacity');
        panel.style.removeProperty('overflow');
        this.panelAnimations.delete(panel);
      });
    }
  }

  customElements.define('aspadz-product-accordion', AspadzProductAccordion);
}
